import { useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { LogOut, Search, Upload, Trash2, ImagePlus } from "lucide-react";
import PlotBoard, { PlotLegend } from "@/components/PlotBoard";
import { Logo } from "@/components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "nova_admin_token";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [authed, setAuthed] = useState(null); // null=checking, true, false
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [plots, setPlots] = useState([]);
  const [busy, setBusy] = useState(null);
  const [query, setQuery] = useState("");
  const [view, setView] = useState("plots");
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [pulse, setPulse] = useState(() => new Set());
  const [cp, setCp] = useState({ current: "", next: "", confirm: "" });
  const [cpLoading, setCpLoading] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [galCaption, setGalCaption] = useState("");
  const [galFile, setGalFile] = useState(null);
  const [galUploading, setGalUploading] = useState(false);
  const [galDeleting, setGalDeleting] = useState(null);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) { setAuthed(false); return; }
    axios.get(`${API}/auth/me`, authHeader)
      .then(() => { setAuthed(true); loadPlots(); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); setAuthed(false); });
    // eslint-disable-next-line
  }, [token]);

  const loadPlots = () => axios.get(`${API}/plots`).then((r) => setPlots(r.data)).catch(() => {});

  const loadLeads = () => {
    setLeadsLoading(true);
    axios.get(`${API}/enquiries`, authHeader)
      .then((r) => setLeads(r.data))
      .catch((err) => { if (err.response?.status === 401) logout(); })
      .finally(() => setLeadsLoading(false));
  };

  const openLeads = () => { setView("leads"); loadLeads(); };

  const loadGallery = () => axios.get(`${API}/gallery`).then((r) => setGallery(r.data)).catch(() => {});
  const openGallery = () => { setView("gallery"); loadGallery(); };

  const uploadGallery = async (e) => {
    e.preventDefault();
    if (!galFile) { toast.error("Please choose an image to upload."); return; }
    setGalUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", galFile);
      fd.append("caption", galCaption);
      await axios.post(`${API}/gallery`, fd, { headers: { ...authHeader.headers, "Content-Type": "multipart/form-data" } });
      toast.success("Photo added to the website gallery.");
      setGalCaption(""); setGalFile(null);
      loadGallery();
    } catch (err) {
      if (err.response?.status === 401) { toast.error("Session expired. Please log in again."); logout(); }
      else toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setGalUploading(false);
    }
  };

  const deleteGalleryItem = async (id) => {
    setGalDeleting(id);
    try {
      await axios.delete(`${API}/gallery/${id}`, authHeader);
      setGallery((g) => g.filter((x) => x.id !== id));
      toast.success("Photo removed.");
    } catch (err) {
      toast.error("Could not remove photo.");
    } finally {
      setGalDeleting(null);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (!cp.current || !cp.next) { toast.error("Please fill in all fields."); return; }
    if (cp.next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (cp.next !== cp.confirm) { toast.error("New passwords do not match."); return; }
    setCpLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: cp.current, new_password: cp.next }, authHeader);
      toast.success("Password updated successfully.");
      setCp({ current: "", next: "", confirm: "" });
    } catch (err) {
      if (err.response?.status === 401) { toast.error("Session expired. Please log in again."); logout(); }
      else toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setCpLoading(false);
    }
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Welcome back, Admin.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); setAuthed(false); };

  const toggle = async (plot) => {
    const cycle = { available: "reserved", reserved: "sold", sold: "available" };
    const next = cycle[plot.status] || "available";
    setBusy(plot.number);
    try {
      const { data } = await axios.patch(`${API}/plots/${plot.number}`, { status: next }, authHeader);
      setPlots((ps) => ps.map((p) => (p.number === data.number ? data : p)));
      toast.success(`Plot ${plot.number} marked ${next.toUpperCase()}`);
      if (next === "sold") {
        setPulse(new Set([plot.number]));
        setTimeout(() => setPulse(new Set()), 2600);
      }
    } catch (err) {
      if (err.response?.status === 401) { toast.error("Session expired. Please log in again."); logout(); }
      else toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const available = plots.filter((p) => p.status === "available").length;
  const reserved = plots.filter((p) => p.status === "reserved").length;
  const sold = plots.filter((p) => p.status === "sold").length;
  const filtered = query ? plots.filter((p) => String(p.number).includes(query.trim())) : plots;

  if (authed === null) {
    return <div className="flex min-h-screen items-center justify-center bg-[var(--cream)] text-[var(--ink-soft)]">Loading…</div>;
  }

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--ink)] px-5">
        <Toaster position="top-center" richColors />
        <div className="w-full max-w-sm">
          <div className="mb-10 flex justify-center"><Logo dark /></div>
          <div className="rounded-sm border border-white/10 bg-white/[0.03] p-8">
            <p className="mb-1 text-xs uppercase tracking-[0.35em] text-[var(--gold-bright)]">Admin Portal</p>
            <h1 className="mb-8 font-serif-display text-3xl text-[var(--cream)]">Sign in</h1>
            <form onSubmit={doLogin} className="space-y-6" data-testid="admin-login-form">
              <input
                type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full border-b border-white/20 bg-transparent py-3 text-[var(--cream)] outline-none placeholder:text-white/40 focus:border-[var(--gold)]"
                data-testid="admin-email"
              />
              <input
                type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full border-b border-white/20 bg-transparent py-3 text-[var(--cream)] outline-none placeholder:text-white/40 focus:border-[var(--gold)]"
                data-testid="admin-password"
              />
              <button
                type="submit" disabled={loading}
                className="w-full rounded-sm bg-[var(--gold)] py-3.5 text-xs uppercase tracking-[0.25em] text-[#1C2427] transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                data-testid="admin-login-btn"
              >
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>
          </div>
          <a href="/" className="mt-6 block text-center text-xs uppercase tracking-[0.2em] text-[var(--cream-3)] hover:text-[var(--gold-bright)]">← Back to website</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <Toaster position="top-center" richColors />
      <header className="sticky top-0 z-40 border-b border-[var(--cream-3)] bg-[var(--cream)]/90 backdrop-blur">
        <div className="flex items-center justify-between px-5 py-4 md:px-10">
          <Logo />
          <div className="flex items-center gap-3">
            <a href="/" className="hidden text-sm text-[var(--ink-soft)] hover:text-[var(--ink)] sm:block link-underline">View Site</a>
            <button onClick={logout} className="flex items-center gap-2 rounded-sm bg-[var(--ink)] px-4 py-2.5 text-xs uppercase tracking-[0.2em] text-[var(--cream)] hover:bg-[var(--gold-hover)]" data-testid="admin-logout">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
        <div className="no-scrollbar flex gap-2 overflow-x-auto px-5 pb-3 md:px-10">
          <button
            onClick={() => setView("plots")}
            className={`shrink-0 whitespace-nowrap rounded-sm px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors ${view === "plots" ? "bg-[var(--ink)] text-[var(--cream)]" : "bg-[var(--cream-2)] text-[var(--ink-soft)] hover:text-[var(--ink)]"}`}
            data-testid="tab-plots"
          >
            Plots
          </button>
          <button
            onClick={openLeads}
            className={`shrink-0 whitespace-nowrap rounded-sm px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors ${view === "leads" ? "bg-[var(--ink)] text-[var(--cream)]" : "bg-[var(--cream-2)] text-[var(--ink-soft)] hover:text-[var(--ink)]"}`}
            data-testid="tab-leads"
          >
            Leads
          </button>
          <button
            onClick={openGallery}
            className={`shrink-0 whitespace-nowrap rounded-sm px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors ${view === "gallery" ? "bg-[var(--ink)] text-[var(--cream)]" : "bg-[var(--cream-2)] text-[var(--ink-soft)] hover:text-[var(--ink)]"}`}
            data-testid="tab-gallery"
          >
            Gallery
          </button>
          <button
            onClick={() => setView("settings")}
            className={`shrink-0 whitespace-nowrap rounded-sm px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors ${view === "settings" ? "bg-[var(--ink)] text-[var(--cream)]" : "bg-[var(--cream-2)] text-[var(--ink-soft)] hover:text-[var(--ink)]"}`}
            data-testid="tab-settings"
          >
            Settings
          </button>
        </div>
      </header>

      {view === "leads" ? (
        <main className="mx-auto max-w-6xl px-5 py-12 md:px-10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-[var(--gold)]">Lead Inbox</p>
              <h1 className="mt-2 font-serif-display text-4xl text-[var(--ink)] md:text-5xl">Enquiry Submissions</h1>
            </div>
            <span className="rounded-sm border border-[var(--cream-3)] bg-white px-4 py-2 text-sm text-[var(--ink-soft)]" data-testid="leads-count">
              {leads.length} total
            </span>
          </div>
          <p className="mt-3 text-[var(--ink-soft)]">Every website enquiry is saved here and also emailed to nmurthygroups@gmail.com.</p>

          {leadsLoading ? (
            <p className="mt-10 text-[var(--ink-soft)]">Loading leads…</p>
          ) : leads.length === 0 ? (
            <p className="mt-10 text-[var(--ink-soft)]" data-testid="leads-empty">No enquiries yet. New submissions will appear here.</p>
          ) : (
            <div className="mt-8 space-y-4" data-testid="leads-list">
              {leads.map((l) => (
                <div key={l.id} className="rounded-sm border border-[var(--cream-3)] bg-white p-6" data-testid={`lead-${l.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-serif-display text-2xl text-[var(--ink)]">{l.name}</p>
                      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-[var(--ink-soft)]">
                        <a href={`tel:${l.phone}`} className="hover:text-[var(--gold-hover)]">{l.phone}</a>
                        <a href={`mailto:${l.email}`} className="hover:text-[var(--gold-hover)]">{l.email}</a>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {l.plot_preference ? (
                        <span className="rounded-sm bg-[var(--cream-2)] px-3 py-1 text-xs uppercase tracking-widest text-[var(--ink)]">Plot {l.plot_preference}</span>
                      ) : null}
                      <span className="text-xs text-[var(--ink-soft)]">{new Date(l.created_at).toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                  {l.message ? <p className="mt-4 border-t border-[var(--cream-3)] pt-4 text-[var(--ink-soft)]">{l.message}</p> : null}
                </div>
              ))}
            </div>
          )}
        </main>
      ) : view === "settings" ? (
        <main className="mx-auto max-w-xl px-5 py-12 md:px-10">
          <p className="text-xs uppercase tracking-[0.35em] text-[var(--gold)]">Settings</p>
          <h1 className="mt-2 font-serif-display text-4xl text-[var(--ink)] md:text-5xl">Change Password</h1>
          <p className="mt-3 text-[var(--ink-soft)]">Update your admin password. You'll keep using your new password to sign in at <strong>/admin</strong>.</p>
          <form onSubmit={changePassword} className="mt-8 space-y-6 rounded-sm border border-[var(--cream-3)] bg-white p-8" data-testid="change-password-form">
            <div>
              <label className="mb-2 block text-xs uppercase tracking-widest text-[var(--ink-soft)]">Current Password</label>
              <input type="password" value={cp.current} onChange={(e) => setCp({ ...cp, current: e.target.value })}
                className="w-full border-b border-[var(--cream-3)] bg-transparent py-2.5 text-[var(--ink)] outline-none focus:border-[var(--gold)]" data-testid="cp-current" />
            </div>
            <div>
              <label className="mb-2 block text-xs uppercase tracking-widest text-[var(--ink-soft)]">New Password</label>
              <input type="password" value={cp.next} onChange={(e) => setCp({ ...cp, next: e.target.value })}
                className="w-full border-b border-[var(--cream-3)] bg-transparent py-2.5 text-[var(--ink)] outline-none focus:border-[var(--gold)]" data-testid="cp-new" />
              <p className="mt-1 text-xs text-[var(--ink-soft)]">Minimum 8 characters.</p>
            </div>
            <div>
              <label className="mb-2 block text-xs uppercase tracking-widest text-[var(--ink-soft)]">Confirm New Password</label>
              <input type="password" value={cp.confirm} onChange={(e) => setCp({ ...cp, confirm: e.target.value })}
                className="w-full border-b border-[var(--cream-3)] bg-transparent py-2.5 text-[var(--ink)] outline-none focus:border-[var(--gold)]" data-testid="cp-confirm" />
            </div>
            <button type="submit" disabled={cpLoading}
              className="w-full rounded-sm bg-[var(--gold)] py-3.5 text-xs uppercase tracking-[0.25em] text-[#1C2427] transition-transform hover:-translate-y-0.5 disabled:opacity-60" data-testid="cp-submit">
              {cpLoading ? "Updating…" : "Update Password"}
            </button>
          </form>
        </main>
      ) : view === "gallery" ? (
        <main className="mx-auto max-w-6xl px-5 py-12 md:px-10">
          <p className="text-xs uppercase tracking-[0.35em] text-[var(--gold)]">Website Gallery</p>
          <h1 className="mt-2 font-serif-display text-4xl text-[var(--ink)] md:text-5xl">Plot & Site Photos</h1>
          <p className="mt-3 text-[var(--ink-soft)]">Upload photos of the plots, roads and amenities. They appear instantly in the Gallery section of your public website.</p>

          <form onSubmit={uploadGallery} className="mt-8 rounded-sm border border-[var(--cream-3)] bg-white p-6" data-testid="gallery-upload-form">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-end">
              <label className="flex-1 cursor-pointer">
                <span className="mb-2 block text-xs uppercase tracking-widest text-[var(--ink-soft)]">Photo (JPG, PNG, WEBP · max 10MB)</span>
                <div className="flex items-center gap-3 rounded-sm border border-dashed border-[var(--cream-3)] px-4 py-3 text-sm text-[var(--ink-soft)] hover:border-[var(--gold)]">
                  <ImagePlus size={18} className="text-[var(--gold)]" />
                  <span data-testid="gallery-file-name">{galFile ? galFile.name : "Choose an image…"}</span>
                </div>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => setGalFile(e.target.files?.[0] || null)} data-testid="gallery-file-input" />
              </label>
              <div className="flex-1">
                <span className="mb-2 block text-xs uppercase tracking-widest text-[var(--ink-soft)]">Caption</span>
                <input
                  value={galCaption} onChange={(e) => setGalCaption(e.target.value)}
                  placeholder="e.g., 60ft concrete main road"
                  className="w-full rounded-sm border border-[var(--cream-3)] px-4 py-3 text-sm outline-none focus:border-[var(--gold)]"
                  data-testid="gallery-caption-input"
                />
              </div>
              <button type="submit" disabled={galUploading}
                className="flex items-center justify-center gap-2 rounded-sm bg-[var(--gold)] px-6 py-3 text-xs uppercase tracking-[0.2em] text-[#1C2427] transition-transform hover:-translate-y-0.5 disabled:opacity-60" data-testid="gallery-upload-btn">
                <Upload size={15} /> {galUploading ? "Uploading…" : "Upload"}
              </button>
            </div>
          </form>

          <div className="mt-10">
            {gallery.length === 0 ? (
              <p className="text-[var(--ink-soft)]" data-testid="gallery-admin-empty">No photos yet. Upload your first plot photo above.</p>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="gallery-admin-grid">
                {gallery.map((img) => (
                  <div key={img.id} className="group relative overflow-hidden rounded-sm border border-[var(--cream-3)] bg-white" data-testid={`gallery-admin-item-${img.id}`}>
                    <img src={`${API}/gallery/${img.id}/file`} alt={img.caption || "plot"} className="h-40 w-full object-cover" />
                    {img.caption ? <p className="px-3 py-2 text-xs text-[var(--ink-soft)]">{img.caption}</p> : null}
                    <button
                      onClick={() => deleteGalleryItem(img.id)}
                      disabled={galDeleting === img.id}
                      className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-rose-600/90 text-white opacity-0 transition-opacity group-hover:opacity-100 disabled:opacity-50"
                      data-testid={`gallery-delete-${img.id}`}
                      aria-label="Delete photo"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      ) : (
      <main className="mx-auto max-w-7xl px-5 py-12 md:px-10">
        <p className="text-xs uppercase tracking-[0.35em] text-[var(--gold)]">Plot Management</p>
        <h1 className="mt-2 font-serif-display text-4xl text-[var(--ink)] md:text-5xl">Nova Royale Availability</h1>
        <p className="mt-3 text-[var(--ink-soft)]">Click a plot to cycle its status: <strong>Available</strong> → <strong>Reserved</strong> → <strong>Sold</strong> → Available. Changes save instantly and reflect on the public website.</p>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[["Total", plots.length, "var(--ink)"], ["Available", available, "#059669"], ["Reserved", reserved, "#d97706"], ["Sold", sold, "#e11d48"]].map(([label, val, color]) => (
            <div key={label} className="rounded-sm border border-[var(--cream-3)] bg-white p-5">
              <p className="font-serif-display text-4xl" style={{ color }}>{val}</p>
              <p className="mt-1 text-xs uppercase tracking-widest text-[var(--ink-soft)]">{label}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <PlotLegend available={available} reserved={reserved} sold={sold} />
          <div className="relative w-full sm:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]" />
            <input
              placeholder="Search plot number…" value={query} onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded-sm border border-[var(--cream-3)] bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-[var(--gold)]"
              data-testid="admin-search"
            />
          </div>
        </div>

        <div className="mt-6">
          <PlotBoard plots={filtered} interactive onToggle={toggle} busyNumber={busy} pulseNumbers={pulse} />
        </div>
      </main>
      )}
    </div>
  );
}
